import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Building2, Home, Landmark, PiggyBank, Phone, Mail, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const FinancePage = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  const services = [
    {
      id: 'bridging',
      title: 'Bridging Loans',
      icon: Building2,
      description: 'Short-term financing solutions to bridge the gap between property transactions.',
      features: [
        'Fast approval process - funds in days, not weeks',
        'Flexible terms from 1 to 24 months',
        'Competitive rates',
        'First and second charge options available',
        'No exit fees on most products'
      ],
      useCases: [
        'Auction purchases requiring quick completion',
        'Chain break situations',
        'Property refurbishment projects',
        'Buy-to-let portfolio expansion'
      ]
    },
    {
      id: 'development',
      title: 'Development Finance',
      icon: Landmark,
      description: 'Funding for ground-up developments, conversions, and major refurbishment projects.',
      features: [
        'Up to 70% of land costs',
        'Up to 100% of build costs',
        'Flexible drawdown schedule',
        'Experienced development finance specialists',
        'Projects from £150k to £50m+'
      ],
      useCases: [
        'New build residential developments',
        'Commercial to residential conversions',
        'Land with planning permission',
        'Heavy refurbishment projects'
      ]
    },
    {
      id: 'commercial',
      title: 'Commercial Mortgage',
      icon: PiggyBank,
      description: 'Tailored mortgage solutions for commercial property purchases and refinancing.',
      features: [
        'Competitive interest rates',
        'Terms up to 25 years',
        'Up to 75% LTV available',
        'Interest-only options',
        'All commercial property types considered'
      ],
      useCases: [
        'Office buildings',
        'Retail units and shops',
        'Industrial and warehouse premises',
        'Mixed-use properties',
        'HMOs and multi-lets'
      ]
    },
    {
      id: 'buy-to-let',
      title: 'Buy to Let Mortgage',
      icon: Home,
      description: 'Specialist mortgages for property investors and landlords.',
      features: [
        'Competitive rates',
        'Up to 80% LTV for standard BTL',
        'Portfolio landlord specialists',
        'Limited company lending available',
        'No minimum income requirements on some products'
      ],
      useCases: [
        'First-time landlords',
        'Portfolio expansion',
        'Remortgaging existing BTL properties',
        'HMO and multi-unit properties',
        'Holiday lets and short-term rentals'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="finance-page">
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-16 md:py-24">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold font-heading mb-6">
              Property Finance Solutions
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Whether you're purchasing at auction, developing property, or expanding your portfolio, 
              we offer comprehensive finance solutions tailored to your needs.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="tel:02034110999">
                <Button className="bg-[#F5A623] hover:bg-[#E59512] text-white">
                  <Phone className="mr-2 h-4 w-4" />
                  Call 020 3411 0999
                </Button>
              </a>
              <a href="mailto:Contact@AuctionGuru.co.uk">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900">
                  <Mail className="mr-2 h-4 w-4" />
                  Email Us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold font-heading text-slate-900 mb-4">
              Our Finance Services
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Expert guidance and competitive rates across all property finance products
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service) => (
              <div 
                key={service.id}
                id={service.id}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 hover:shadow-md transition-shadow scroll-mt-24 text-center"
                data-testid={`finance-${service.id}`}
              >
                <div className="flex flex-col items-center gap-4 mb-6">
                  <div className="p-3 bg-[#F5A623]/10 rounded-lg">
                    <service.icon className="h-8 w-8 text-[#F5A623]" />
                  </div>
                  <h3 className="text-2xl font-bold font-heading text-slate-900">
                    {service.title}
                  </h3>
                </div>

                <p className="text-slate-600 mb-6">
                  {service.description}
                </p>

                <div className="mb-6">
                  <h4 className="font-semibold text-slate-900 mb-3">Key Features:</h4>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center justify-center gap-2 text-sm text-slate-600">
                        <ArrowRight className="h-4 w-4 text-[#F5A623] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Ideal For:</h4>
                  <div className="flex flex-wrap justify-center gap-2">
                    {service.useCases.map((useCase, idx) => (
                      <span 
                        key={idx}
                        className="px-3 py-1 bg-slate-100 text-slate-700 text-sm rounded-full"
                      >
                        {useCase}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#F5A623] py-16">
        <div className="container mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl font-bold font-heading text-white mb-4">
            Ready to Discuss Your Finance Options?
          </h2>
          <p className="text-white/90 mb-8 max-w-2xl mx-auto">
            Our experienced team is here to help you find the right finance solution for your property investment.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:02034110999">
              <Button size="lg" className="bg-slate-900 hover:bg-slate-800 text-white">
                <Phone className="mr-2 h-5 w-5" />
                020 3411 0999
              </Button>
            </a>
            <a href="mailto:Contact@AuctionGuru.co.uk">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900">
                <Mail className="mr-2 h-5 w-5" />
                Contact@AuctionGuru.co.uk
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FinancePage;
